/**
 * Reader reviews on book pages (issue #89), as the API sends and accepts them.
 *
 *   GET    /api/reviews/:world/:work          the visible reviews, newest first
 *   PUT    /api/reviews/:world/:work          the signed-in reader's own review
 *   DELETE /api/reviews/:world/:work          the reader deletes their own
 *   POST   /api/reviews/:world/report/:id     anyone reports a review
 *   POST   /api/reviews/:world/remove/:id     the maintainer removes one, with a reason
 *
 * Published at once: there is no queue. Removal is reactive, recorded with its
 * reason, and the author is emailed that reason (the DSA's statement of
 * reasons). A removed review is never shown.
 */

import { z } from 'zod'
import type { MessageKey } from './message-keys.ts'

export const REVIEW_MAX_LENGTH = 6000
export const REASON_MAX_LENGTH = 1000

const key = (k: MessageKey): string => k

export const reviewInputSchema = z.object({
  body: z
    .string({ error: key('error.review_required') })
    .trim()
    .min(1, { error: key('error.review_required') })
    .max(REVIEW_MAX_LENGTH, { error: key('error.review_too_long') }),
  spoilers: z.boolean({ error: key('error.bad_request') }),
})
export type ReviewInput = z.infer<typeof reviewInputSchema>

export const reportInputSchema = z.object({
  reason: z
    .string({ error: key('error.bad_request') })
    .trim()
    .max(REASON_MAX_LENGTH, { error: key('error.reason_too_long') })
    .transform((reason) => (reason === '' ? null : reason))
    .nullable(),
  turnstileToken: z.string({ error: key('error.turnstile_missing') }).min(1, { error: key('error.turnstile_missing') }),
})

export const removeInputSchema = z.object({
  reason: z
    .string({ error: key('error.reason_required') })
    .trim()
    .min(1, { error: key('error.reason_required') })
    .max(REASON_MAX_LENGTH, { error: key('error.reason_too_long') }),
})

export interface PublicReview {
  id: number
  body: string
  spoilers: boolean
  /** The reviewer's nickname, or null for none (shown as „Читател“). */
  author: string | null
  created_at: string
  updated_at: string
  /** The signed-in reader's own review. */
  mine: boolean
}

export interface ReviewsResponse {
  reviews: PublicReview[]
  /** Whether the signed-in reader is the maintainer, who can remove reviews. */
  moderator: boolean
  /** Whether the signed-in reader may write or edit a review; null when signed out. */
  writing: WritingState | null
}

/**
 * The strike rule (issue #89, decided 30 Sep).
 *
 * A strike is a review of the reader's that the maintainer removed, on any
 * book. Strikes are not stored: they are the reader's reviews with a
 * `removed_at`, which are kept until the account is deleted. Deleting the
 * account and registering again resets them; closing that would need the
 * address of a deleted account, which the privacy page promises is not kept.
 *
 *   0 or 1 strike   writing allowed (a removal frees the slot for that book)
 *   2 strikes       no writing or editing for 30 days from the second
 *                   removal's removed_at
 *   3 or more       no writing or editing, for good. The account, its shelf,
 *                   ratings, notes and quiz result are untouched, and the
 *                   reader can still delete their own reviews.
 */
export const SUSPENSION_DAYS = 30
const DAY_MS = 24 * 60 * 60 * 1000

export type WritingState = { state: 'allowed' } | { state: 'suspended'; until: string } | { state: 'banned' }

/** The reader's writing state, from the times their reviews were removed (any order). */
export function writingState(removedAt: readonly string[], now: Date): WritingState {
  if (removedAt.length >= 3) return { state: 'banned' }
  if (removedAt.length === 2) {
    const latest = Math.max(...removedAt.map((at) => new Date(at).getTime()))
    const until = latest + SUSPENSION_DAYS * DAY_MS
    if (now.getTime() < until) return { state: 'suspended', until: new Date(until).toISOString() }
  }
  return { state: 'allowed' }
}

/** When a suspension ends, in the reader's language, on Sofia's clock: the page and the email say the same. */
export function suspensionEnds(locale: 'bg' | 'en', until: string): string {
  return new Intl.DateTimeFormat(locale === 'bg' ? 'bg-BG' : 'en-GB', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'Europe/Sofia',
  }).format(new Date(until))
}

/**
 * The reader's own live reviews in one world, for the account page (#118).
 * Only what a list needs: which book, and when. No review text: the list
 * links to the book page, where the review is shown, and a removed review is
 * not in the list at all.
 */
export interface OwnReview {
  id: number
  /** The book's slug in this world. */
  work: string
  created_at: string
  updated_at: string
}

export interface OwnReviewsResponse {
  reviews: OwnReview[]
}

/**
 * Publishing a private note as a review (#107).
 *
 *   POST /api/reviews/:world/publish-note/:work    { spoilers }
 *
 * The note's text becomes the reader's live review of the book, and the note
 * is cleared; a shelf entry that held only the note is deleted, because an
 * entry exists only while it holds something (migration 0011's CHECK).
 *
 * With a live review already there, the note is appended to it after a blank
 * line, never replacing it. With none, a review is created. A removed review
 * is not live, so it is never appended to: the slot is free and a new review
 * is created.
 *
 * The resulting length is checked before anything is written, and the writes
 * are one atomic batch, so a refusal leaves both the note and the review as
 * they were.
 */
export const NOTE_SEPARATOR = '\n\n'

export type PublishMode = 'create' | 'append'

/** What publishing would do, and the review's text afterwards. The server and the page compute it the same way. */
export function publishedBody(liveReview: string | null, note: string): { mode: PublishMode; body: string } {
  return liveReview === null ? { mode: 'create', body: note } : { mode: 'append', body: `${liveReview}${NOTE_SEPARATOR}${note}` }
}

export const publishNoteInputSchema = z.object({
  spoilers: z.boolean({ error: key('error.bad_request') }),
})

export interface PublishNoteResponse {
  mode: PublishMode
  /**
   * What happened to the shelf entry: the note cleared; the entry deleted,
   * because the note was all it held; or kept, when the note was changed
   * between the read and the write and the newer one is left alone.
   */
  entry: 'cleared' | 'deleted' | 'kept'
}
